import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Building2, Check, CheckCheck, ListChecks, MessageSquare, RefreshCw, Siren, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import {
  Badge,
  Chip,
  EmptyState,
  GHOST_BTN,
  ICON_TILES,
  InfoHint,
  InfoTooltip,
  PRIMARY_BTN_SM,
  SECONDARY_BTN,
  SectionCard,
} from "../components/designSystem";

// Le notifiche arrivano già divise per natura (backend, CATEGORIE_NOTIFICA in
// server.py): cose da fare, comunicazioni, avvisi. Prima stavano in un'unica
// lista e le cose da fare si confondevano con il resto finché il badge non
// diceva più niente. Una cosa da fare non si chiude leggendola: si chiude da
// sola quando il lavoro è fatto, oppure con "Fatto".

// Oltre queste ore una cosa da fare è in ritardo: stessa soglia del badge rosso
// e del promemoria via email (ORE_PROMEMORIA_NOTIFICHE nel backend).
const ORE_RITARDO = 48;

// Sezioni aperte: ricordate per chi guarda, in questo browser. Senza una
// scelta salvata, Da fare e Comunicazioni aperte e Avvisi chiusa: gli avvisi
// sono informazioni da consultare, non da smaltire.
const CHIAVE_SEZIONI = "notifiche.sezioniAperte";
const SEZIONI_DEFAULT = { daFare: true, comunicazioni: true, avvisi: false };

function leggiSezioni() {
  try {
    return { ...SEZIONI_DEFAULT, ...JSON.parse(localStorage.getItem(CHIAVE_SEZIONI) || "{}") };
  } catch (e) {
    return SEZIONI_DEFAULT;
  }
}

function oreDa(iso) {
  return iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : 0;
}

function formatRelative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}g fa`;
  return new Date(iso).toLocaleDateString("it-IT");
}

// Evidenzia il nome del cliente dentro il testo della notifica: il messaggio è
// testo libero generato dal backend, quindi si spezza sulle occorrenze esatte
// di client_name invece di aspettarsi un markup.
function evidenziaCliente(message, clientName) {
  if (!clientName || !message || !message.includes(clientName)) return message;
  return message.split(clientName).flatMap((parte, i) => (
    i === 0 ? [parte] : [<strong key={i} className="font-semibold text-[#15161c]">{clientName}</strong>, parte]
  ));
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ da_fare: [], comunicazioni: [], avvisi: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mostraLette, setMostraLette] = useState(false);
  const [aperte, setAperte] = useState(leggiSezioni);

  const toggleSezione = (chiave) => setAperte((prev) => {
    const next = { ...prev, [chiave]: !prev[chiave] };
    try {
      localStorage.setItem(CHIAVE_SEZIONI, JSON.stringify(next));
    } catch (e) {/* senza storage la scelta vale per questa visita */}
    return next;
  });

  const fetchNotifications = async () => {
    setRefreshing(true);
    try {
      const res = await apiFetch("/notifications");
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("Errore nel caricamento delle notifiche");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Dopo ogni modifica: ricarica la pagina e avvisa il badge della navigazione.
  const aggiorna = () => {
    fetchNotifications();
    window.dispatchEvent(new Event("notifiche-aggiornate"));
  };

  const chiama = async (path, method, okMsg, errMsg = "Errore") => {
    try {
      const res = await apiFetch(path, { method });
      if (res.ok) {
        if (okMsg) toast.success(okMsg);
        aggiorna();
      } else {
        toast.error(errMsg);
      }
    } catch (e) {
      toast.error(errMsg);
    }
  };

  const handleMarkRead = (id) => chiama(`/notifications/${id}/read`, "PATCH");
  const handleDone = (id) => chiama(`/notifications/${id}/fatto`, "PATCH", "Segnata come fatta");
  const handleDelete = (id) => chiama(`/notifications/${id}`, "DELETE", "Notifica eliminata", "Errore eliminazione");
  const handleMarkAllRead = () => chiama("/notifications/read-all", "PATCH", "Tutte le notifiche segnate come lette");

  const handleOpen = (notif) => {
    if (!notif.read) handleMarkRead(notif.notification_id);
    if (notif.link) navigate(notif.link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-[#5b5bd6]" />
      </div>
    );
  }

  const daFare = data.da_fare || [];
  const comunicazioni = data.comunicazioni || [];
  const avvisi = data.avvisi || [];
  const nonLette = comunicazioni.filter((n) => !n.read);
  const lette = comunicazioni.filter((n) => n.read);
  const inRitardo = daFare.filter((n) => oreDa(n.created_at) > ORE_RITARDO).length;
  const daLeggere = [...comunicazioni, ...avvisi].some((n) => !n.read);
  const totale = daFare.length + nonLette.length;
  const azioni = { onOpen: handleOpen, onRead: handleMarkRead, onDone: handleDone, onDelete: handleDelete };

  return (
    <div className="flex flex-col gap-[18px] animate-fade-in scheda-font-ui" data-testid="notifications-page">
      {/* Testata */}
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)] px-5 py-4 flex items-center gap-4 flex-wrap">
        <div className="w-[42px] h-[42px] rounded-[12px] flex items-center justify-center shrink-0" style={{ background: ICON_TILES.viola.bg }}>
          <Bell className="w-5 h-5" style={{ color: ICON_TILES.viola.color }} strokeWidth={1.85} />
        </div>
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-[#15161c] flex items-center gap-2">
            Notifiche
            {totale > 0 && (
              <span className="h-[22px] px-[9px] rounded-full bg-[#eeeefc] text-[#5b5bd6] text-[11.5px] font-semibold scheda-font-mono inline-flex items-center" data-testid="header-unread-count">
                {totale}
              </span>
            )}
          </h1>
          <div className="text-[12.5px] text-[#9095a1]">
            {daFare.length} da fare · {nonLette.length} da leggere
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <button type="button" onClick={fetchNotifications} disabled={refreshing} className={SECONDARY_BTN} data-testid="refresh-notifs-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
          {daLeggere && (
            <InfoTooltip title="Segna tutte lette" description="Vale per comunicazioni e avvisi. Le cose da fare restano finché il lavoro non è fatto.">
              <button type="button" onClick={handleMarkAllRead} className={SECONDARY_BTN} data-testid="mark-all-read-btn">
                <CheckCheck className="w-3.5 h-3.5" />
                Segna tutte lette
              </button>
            </InfoTooltip>
          )}
        </div>
      </div>

      <SectionCard
        icon={ListChecks}
        iconBg={ICON_TILES.viola.bg}
        iconColor={ICON_TILES.viola.color}
        title="Da fare"
        count={daFare.length}
        collapsible
        open={aperte.daFare}
        onToggle={() => toggleSezione("daFare")}
        action={
          <div className="flex items-center gap-2">
            {inRitardo > 0 && <Badge tone="danger" dot testId="da-fare-in-ritardo">{inRitardo} in ritardo</Badge>}
            <InfoHint
              title="Cose da fare"
              description="Si chiudono da sole quando il lavoro è fatto (pagamento registrato, team assegnato, fattura emessa…), per tutti quelli che le hanno ricevute. Se non succede, usa Fatto."
              testId="da-fare-info"
            />
          </div>
        }
        dense
        testId="sezione-da-fare"
      >
        {daFare.length === 0 ? (
          <div data-testid="notifs-empty"><EmptyState>Niente da fare: tutto in ordine.</EmptyState></div>
        ) : (
          <ListaNotifiche notifiche={daFare} {...azioni} />
        )}
      </SectionCard>

      <SectionCard
        icon={MessageSquare}
        iconBg={ICON_TILES.arancio.bg}
        iconColor={ICON_TILES.arancio.color}
        title="Comunicazioni"
        count={nonLette.length}
        collapsible
        open={aperte.comunicazioni}
        onToggle={() => toggleSezione("comunicazioni")}
        action={lette.length > 0 && (
          <button type="button" onClick={() => setMostraLette((v) => !v)} className={GHOST_BTN} data-testid="mostra-lette-btn">
            {mostraLette ? "Nascondi le lette" : `Mostra anche le lette (${lette.length})`}
          </button>
        )}
        dense
        testId="sezione-comunicazioni"
      >
        {nonLette.length === 0 && !mostraLette ? (
          <div data-testid="notifs-empty"><EmptyState>Nessuna minuta da leggere.</EmptyState></div>
        ) : (
          <ListaNotifiche notifiche={mostraLette ? comunicazioni : nonLette} {...azioni} />
        )}
      </SectionCard>

      <SectionCard
        icon={Siren}
        iconBg={ICON_TILES.neutro.bg}
        iconColor={ICON_TILES.neutro.color}
        title="Avvisi"
        count={avvisi.length}
        collapsible
        open={aperte.avvisi}
        onToggle={() => toggleSezione("avvisi")}
        action={
          <InfoHint
            title="Avvisi"
            description="Informazioni dal sistema: clienti critici, scadenze, pagamenti. Scadono da sole dopo 14 giorni e non contano nel numero accanto a Notifiche."
            testId="avvisi-info"
          />
        }
        dense
        testId="sezione-avvisi"
      >
        {avvisi.length === 0 ? (
          <div data-testid="notifs-empty"><EmptyState>Nessun avviso nelle ultime due settimane.</EmptyState></div>
        ) : (
          <ListaNotifiche notifiche={avvisi} {...azioni} />
        )}
      </SectionCard>
    </div>
  );
}

function ListaNotifiche({ notifiche, ...azioni }) {
  return (
    <div className="flex flex-col border-t border-[#f0f0f5]" data-testid="notifs-list">
      {notifiche.map((n) => <RigaNotifica key={n.notification_id} notif={n} {...azioni} />)}
    </div>
  );
}

function IconAction({ label, description, onClick, testId, children }) {
  return (
    <InfoTooltip title={label} description={description}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="h-8 w-8 rounded-[8px] inline-flex items-center justify-center text-[#9095a1] hover:bg-[#f3f3f8] hover:text-[#565d6d] transition-colors"
        data-testid={testId}
      >
        {children}
      </button>
    </InfoTooltip>
  );
}

function RigaNotifica({ notif, onOpen, onRead, onDone, onDelete }) {
  const id = notif.notification_id;
  const isAzione = notif.categoria === "azione";
  const isReport = notif.notif_type === "critical_clients_weekly" || notif.type === "critical_clients_weekly";
  const inRitardo = isAzione && oreDa(notif.created_at) > ORE_RITARDO;
  // Una cosa da fare resta in evidenza anche se letta: è aperta.
  const evidenza = isAzione || !notif.read;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f0f0f5] last:border-b-0 hover:bg-[#fbfbfd] transition-colors scheda-row-anim" data-testid={`notif-${id}`}>
      <span
        className={`mt-[7px] w-2 h-2 rounded-full shrink-0 ${!notif.read ? "bg-[#5b5bd6]" : "bg-transparent"}`}
        data-testid={!notif.read ? "unread-dot" : undefined}
      />
      <button type="button" onClick={() => onOpen(notif)} className="flex-1 min-w-0 text-left flex flex-col gap-1" data-testid={`notif-open-${id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {notif.client_name && (
            <span data-testid={`notif-cliente-${id}`}>
              <Chip icon={Building2} size="sm">{notif.client_name}</Chip>
            </span>
          )}
          {notif.is_alert && <Badge tone="danger" dot>Alert</Badge>}
          {inRitardo && <Badge tone="warning" dot>In ritardo</Badge>}
        </div>
        <p className={`text-[13.5px] ${evidenza ? "font-semibold text-[#15161c]" : "font-medium text-[#565d6d]"}`}>{notif.title}</p>
        <p className={`text-[12.5px] text-[#565d6d] ${isReport ? "whitespace-pre-line" : "line-clamp-2"}`}>
          {evidenziaCliente(notif.message, notif.client_name)}
        </p>
        <p className="text-[11.5px] text-[#9095a1] scheda-font-mono">{formatRelative(notif.created_at)}</p>
      </button>
      <div className="flex items-center gap-1 shrink-0">
        {isAzione ? (
          <InfoTooltip title="Segna come fatta" description="La chiude per tutti quelli che l'hanno ricevuta.">
            <button type="button" onClick={() => onDone(id)} className={PRIMARY_BTN_SM} data-testid={`notif-fatto-${id}`}>
              <Check className="w-3.5 h-3.5" />
              Fatto
            </button>
          </InfoTooltip>
        ) : (
          <>
            {!notif.read && (
              <IconAction label="Segna come letta" onClick={() => onRead(id)} testId={`mark-read-${id}`}>
                <Check className="w-4 h-4 text-[#0f7040]" />
              </IconAction>
            )}
            <IconAction label="Elimina" onClick={() => onDelete(id)} testId={`delete-notif-${id}`}>
              <Trash2 className="w-4 h-4 text-[#c0392b]" />
            </IconAction>
          </>
        )}
      </div>
    </div>
  );
}
